import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useScrollLock } from '@/lib/experience';
import { whatsappLink } from '@/data/site';
import { Paw, WhatsAppIcon, Sparkle } from './brand/Shapes';

const PlanModalContext = createContext({ openPlan: () => {} });
export const usePlanModal = () => useContext(PlanModalContext);

const EMPTY = { nome: '', nomePet: '', idade: '', raca: '' };

const Field = ({ label, className = '', ...props }) => (
  <label className={`block ${className}`}>
    <span className="block text-sm font-extrabold text-pet-muted mb-1.5 ml-1">{label}</span>
    <input
      {...props}
      required
      className="w-full h-12 px-4 rounded-2xl bg-pet-cream border-2 border-pet-blue/15 font-semibold text-pet-ink placeholder:text-pet-muted/60 focus:outline-none focus:border-pet-blue focus:ring-4 focus:ring-pet-blue/15 transition"
    />
  </label>
);

const ContactModal = ({ plan, onClose }) => {
  const [step, setStep] = useState('choice');
  const [form, setForm] = useState(EMPTY);
  const firstButton = useRef(null);
  const open = Boolean(plan);

  useScrollLock(open);

  useEffect(() => {
    if (!open) return undefined;
    setStep('choice');
    setForm(EMPTY);
    const t = setTimeout(() => firstButton.current?.focus(), 250);
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  const goDirect = () => {
    window.open(whatsappLink(`Olá! Tenho interesse, gostaria de mais informações sobre: ${plan}.`), '_blank', 'noopener');
    onClose();
  };

  const submit = (e) => {
    e.preventDefault();
    window.open(
      whatsappLink(
        `Olá! Sou ${form.nome}, meu pet é o ${form.nomePet} com ${form.idade} anos da raça ${form.raca}. Tenho interesse em: ${plan}. Gostaria de mais informações!`
      ),
      '_blank',
      'noopener'
    );
    onClose();
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[120] flex items-end md:items-center justify-center">
          <motion.div
            className="absolute inset-0 bg-pet-blue/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
            data-lenis-prevent
            className="relative w-full md:max-w-[460px] max-h-[92svh] overflow-y-auto rounded-t-[36px] md:rounded-[40px] bg-white text-pet-ink shadow-2xl pb-[max(1.5rem,env(safe-area-inset-bottom))]"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 260, damping: 24 }}
          >
            <div className="md:hidden mx-auto mt-3 h-1.5 w-12 rounded-full bg-pet-blue/20" aria-hidden="true" />
            <button
              type="button"
              onClick={onClose}
              aria-label="Fechar"
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-pet-cream text-pet-blue font-bold flex items-center justify-center text-xl leading-none hover:bg-pet-pink-soft transition-colors"
            >
              ×
            </button>

            <div className="px-6 pt-8 md:px-8 md:pt-10">
              <AnimatePresence mode="wait" initial={false}>
                {step === 'choice' ? (
                  <motion.div key="choice" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.3 }}>
                    <span className="w-14 h-14 rounded-full bg-pet-blue text-white flex items-center justify-center heartbeat">
                      <Paw className="w-7 h-7" />
                    </span>
                    <p className="kicker bg-pet-pink-soft text-pet-pink-ink mt-6">Você escolheu 💙</p>
                    <h3 id="modal-title" className="font-display text-[2rem] leading-[1.02] mt-2 pr-8">{plan}</h3>
                    <p className="mt-4 font-semibold text-pet-muted leading-relaxed">
                      Deseja responder um pequeno formulário para otimizar seu contato?
                    </p>
                    <div className="mt-7 flex flex-col gap-3">
                      <button ref={firstButton} type="button" onClick={() => setStep('form')} className="btn-blue w-full">
                        <Sparkle className="w-4 h-4" /> Sim, quero preencher!
                      </button>
                      <button type="button" onClick={goDirect} className="btn-pink w-full pl-2.5">
                        <span className="w-9 h-9 rounded-full bg-pet-whats text-white flex items-center justify-center">
                          <WhatsAppIcon className="w-5 h-5" />
                        </span>
                        Não, ir direto ao WhatsApp
                      </button>
                    </div>
                  </motion.div>
                ) : (
                  <motion.form key="form" onSubmit={submit} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
                    <p className="kicker bg-pet-blue-soft text-pet-blue-deep">{plan}</p>
                    <h3 id="modal-title" className="font-display text-[2rem] leading-[1.02] mt-2 pr-8">
                      Conte-nos sobre você e seu pet!
                    </h3>
                    <div className="mt-6 space-y-4">
                      <Field label="Seu nome" value={form.nome} onChange={set('nome')} placeholder="Digite seu nome" autoComplete="name" autoFocus />
                      <Field label="Nome do pet" value={form.nomePet} onChange={set('nomePet')} placeholder="Nome do seu pet" />
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Idade (anos)" value={form.idade} onChange={set('idade')} placeholder="Ex: 3" inputMode="numeric" />
                        <Field label="Raça" value={form.raca} onChange={set('raca')} placeholder="Ex: Poodle" />
                      </div>
                    </div>
                    <button type="submit" className="btn-blue w-full mt-7 pl-2.5">
                      <span className="w-9 h-9 rounded-full bg-pet-whats flex items-center justify-center">
                        <WhatsAppIcon className="w-5 h-5" />
                      </span>
                      Enviar pelo WhatsApp
                    </button>
                    <button type="button" onClick={() => setStep('choice')} className="w-full py-3 mt-1 text-sm font-bold text-pet-muted hover:text-pet-ink">
                      ← Voltar
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export const PlanModalProvider = ({ children }) => {
  const [plan, setPlan] = useState(null);
  const close = useCallback(() => setPlan(null), []);
  const value = useMemo(() => ({ openPlan: setPlan }), []);
  return (
    <PlanModalContext.Provider value={value}>
      {children}
      <ContactModal plan={plan} onClose={close} />
    </PlanModalContext.Provider>
  );
};

export default PlanModalProvider;
